<template>
  <div
    :class="[
      {
        'el-table--fit': fit,
        'el-table--striped': stripe,
        'el-table--border': border || isGroup,
        'el-table--hidden': isHidden,
        'el-table--group': isGroup,
        'el-table--fluid-height': maxHeight,
        'el-table--scrollable-x': layout.scrollX,
        'el-table--scrollable-y': layout.scrollY,
        'el-table--enable-row-hover': !store.states.isComplex,
        'el-table--enable-row-transition':
          (store.states.data || []).length !== 0 &&
          (store.states.data || []).length < 100
      },
      tableSize ? `el-table--${tableSize}` : ''
    ]"
    class="el-table"
    @mouseleave="handleMouseLeave($event)"
  >
    <div
      ref="hiddenColumns"
      class="hidden-columns"
    >
      <slot></slot>
    </div>
    <div
      v-if="showHeader"
      ref="headerWrapper"
      v-mousewheel="handleHeaderFooterMousewheel"
      class="el-table__header-wrapper"
      :rt-xpc-sticky="headerSticky"
      rt-xpc-sticky-group="rtTableHeader"
    >
      <table-header
        ref="tableHeader"
        :store="store"
        :border="border"
        :default-sort="defaultSort"
        :style="{
          width: layout.bodyWidth ? layout.bodyWidth + 'px' : ''
        }"
      >
      </table-header>
    </div>
    <div
      ref="tableLoadingRef"
      v-loading="loading"
      class="rt-table-loading"
      :class="headerSticky ? 'is-sticky' : ''"
    ></div>
    <div
      ref="bodyWrapper"
      :class="[
        layout.scrollX ? `is-scrolling-${scrollPosition}` : 'is-scrolling-none'
      ]"
      :style="[bodyHeight]"
      class="el-table__body-wrapper"
    >
      <table-body
        :context="context"
        :store="store"
        :stripe="stripe"
        :row-class-name="rowClassName"
        :row-style="rowStyle"
        :highlight="highlightCurrentRow"
        :style="{
          width: bodyWidth
        }"
      >
      </table-body>
      <div
        v-if="!data || data.length === 0"
        class="rt-table-empty"
      >
        <slot name="empty">
          <rt-default-image
            v-if="!data || data.length === 0"
            text
          >
            <div slot="describe">
              {{ emptyText || $tx('暂无数据', 'xpc.uploadfile.nodata') }}
            </div>
          </rt-default-image>
        </slot>
      </div>
      <div
        v-if="$slots.append"
        ref="appendWrapper"
        class="el-table__append-wrapper"
      >
        <slot name="append"></slot>
      </div>
    </div>
    <div
      v-if="!data || data.length === 0"
      class="rt-table-empty"
      :style="{ top: `${layout.headerHeight}px` }"
    >
      <slot name="empty">
        <rt-default-image text>
          <div slot="describe">
            {{ emptyText || $tx('暂无数据', 'xpc.uploadfile.nodata') }}
          </div>
        </rt-default-image>
      </slot>
    </div>
    <div
      v-if="showSummary"
      v-show="data && data.length > 0"
      ref="footerWrapper"
      v-mousewheel="handleHeaderFooterMousewheel"
      class="el-table__footer-wrapper"
    >
      <table-footer
        :store="store"
        :border="border"
        :sum-text="sumText || t('el.table.sumText')"
        :summary-method="summaryMethod"
        :default-sort="defaultSort"
        :style="{
          width: layout.bodyWidth ? layout.bodyWidth + 'px' : ''
        }"
      >
      </table-footer>
    </div>
    <div
      v-if="fixedColumns.length > 0"
      ref="fixedWrapper"
      v-mousewheel="handleFixedMousewheel"
      :style="[
        {
          width: layout.fixedWidth ? layout.fixedWidth - 1 + 'px' : '',
          visibility: showFixColumns ? 'visible' : 'hidden'
        },
        fixedHeight
      ]"
      class="el-table__fixed"
      :class="{ 'has-optimize-fixed': hasOptimizeFixed }"
    >
      <div
        v-if="showHeader"
        ref="fixedHeaderWrapper"
        class="el-table__fixed-header-wrapper"
        :rt-xpc-sticky="headerSticky"
        rt-xpc-sticky-group="rtTableHeader"
        :style="{ height: layout.headerHeight + 'px' }"
      >
        <table-header
          ref="fixedTableHeader"
          :border="border"
          :store="store"
          :style="{
            width: hasOptimizeFixed ? layout.fixedWidth : bodyWidth
          }"
          fixed="left"
        ></table-header>
      </div>
      <div
        ref="fixedBodyWrapper"
        :style="[
          {
            top: layout.headerHeight + 'px'
          },
          fixedBodyHeight
        ]"
        class="el-table__fixed-body-wrapper"
      >
        <table-body
          :store="store"
          :stripe="stripe"
          :highlight="highlightCurrentRow"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
          :style="{
            width: hasOptimizeFixed ? layout.fixedWidth : bodyWidth
          }"
          fixed="left"
        >
        </table-body>
        <div
          v-if="$slots.append"
          :style="{ height: layout.appendHeight + 'px' }"
          class="el-table__append-gutter"
        ></div>
      </div>
      <div
        v-if="showSummary"
        v-show="data && data.length > 0"
        ref="fixedFooterWrapper"
        class="el-table__fixed-footer-wrapper"
      >
        <table-footer
          :border="border"
          :sum-text="sumText || t('el.table.sumText')"
          :summary-method="summaryMethod"
          :store="store"
          :style="{
            width: bodyWidth
          }"
          fixed="left"
        ></table-footer>
      </div>
    </div>
    <div
      v-if="rightFixedColumns.length > 0"
      ref="rightFixedWrapper"
      v-mousewheel="handleFixedMousewheel"
      :style="[
        {
          width: layout.rightFixedWidth ? layout.rightFixedWidth + 'px' : '',
          right:
            layout.scrollY && !headerSticky
              ? (border ? layout.gutterWidth : layout.gutterWidth || 0) + 'px'
              : '',
          visibility: showFixColumns ? 'visible' : 'hidden'
        },
        fixedHeight
      ]"
      class="el-table__fixed-right"
      :class="{ 'has-optimize-fixed': hasOptimizeFixed }"
    >
      <div
        v-if="showHeader"
        ref="rightFixedHeaderWrapper"
        class="el-table__fixed-header-wrapper"
        :rt-xpc-sticky="headerSticky"
        rt-xpc-sticky-group="rtTableHeader"
        :style="{ height: layout.headerHeight + 'px' }"
      >
        <table-header
          ref="rightFixedTableHeader"
          :border="border"
          :store="store"
          :style="{
            width: hasOptimizeFixed ? layout.rightFixedWidth : bodyWidth
          }"
          fixed="right"
        ></table-header>
      </div>
      <div
        ref="rightFixedBodyWrapper"
        :style="[
          {
            top: layout.headerHeight + 'px'
          },
          fixedBodyHeight
        ]"
        class="el-table__fixed-body-wrapper"
      >
        <table-body
          :store="store"
          :stripe="stripe"
          :row-class-name="rowClassName"
          :row-style="rowStyle"
          :highlight="highlightCurrentRow"
          :style="{
            width: hasOptimizeFixed ? layout.rightFixedWidth : bodyWidth
          }"
          fixed="right"
        >
        </table-body>
        <div
          v-if="$slots.append"
          :style="{ height: layout.appendHeight + 'px' }"
          class="el-table__append-gutter"
        ></div>
      </div>
      <div
        v-if="showSummary"
        v-show="data && data.length > 0"
        ref="rightFixedFooterWrapper"
        class="el-table__fixed-footer-wrapper"
      >
        <table-footer
          :border="border"
          :sum-text="sumText || t('el.table.sumText')"
          :summary-method="summaryMethod"
          :store="store"
          :style="{
            width: bodyWidth
          }"
          fixed="right"
        ></table-footer>
      </div>
    </div>
    <div
      v-if="rightFixedColumns.length > 0"
      ref="rightFixedPatch"
      :style="{
        width: layout.scrollY ? layout.gutterWidth + 'px' : '0',
        height: layout.headerHeight + 'px'
      }"
      class="el-table__fixed-right-patch"
    ></div>
    <div
      v-show="resizeProxyVisible"
      ref="resizeProxy"
      class="el-table__column-resize-proxy"
    ></div>
  </div>
</template>

<script type="text/babel">
import { debounce, throttle } from 'throttle-debounce';
import {
  addResizeListener,
  removeResizeListener
} from 'element-ui/lib/utils/resize-event';
import Mousewheel from 'element-ui/lib/directives/mousewheel';
import Locale from 'element-ui/lib/mixins/locale';
import Migrating from 'element-ui/lib/mixins/migrating';
import { createStore, mapStates } from './store/helper';
import TableLayout from './table-layout';
import TableBody from './table-body';
import TableHeader from './table-header';
import TableFooter from './table-footer';
import { parseHeight } from './util';
import { closest } from 'vue-xpc/src/utils/util';
import { stickyItem } from 'vue-xpc/src/mixins/sticky';

let tableIdSeed = 1;

export default {
  name: 'ElTable',

  directives: {
    Mousewheel
  },

  components: {
    TableHeader,
    TableFooter,
    TableBody
  },

  mixins: [Locale, Migrating, stickyItem],

  props: {
    data: {
      type: Array,
      default: function () {
        return [];
      }
    },

    size: String,

    width: [String, Number],

    height: [String, Number],

    maxHeight: [String, Number],

    fit: {
      type: Boolean,
      default: true
    },

    stripe: Boolean,

    border: Boolean,

    rowKey: [String, Function],

    // eslint-disable-next-line
    context: {},

    showHeader: {
      type: Boolean,
      default: true
    },

    showSummary: Boolean,

    sumText: String,

    summaryMethod: Function,

    rowClassName: [String, Function],

    rowStyle: [Object, Function],

    cellClassName: [String, Function],

    cellStyle: [Object, Function],

    headerRowClassName: [String, Function],

    headerRowStyle: [Object, Function],

    headerCellClassName: [String, Function],

    headerCellStyle: [Object, Function],

    highlightCurrentRow: Boolean,

    currentRowKey: [String, Number],

    emptyText: String,

    expandRowKeys: Array,

    defaultExpandAll: Boolean,

    defaultSort: Object,

    tooltipEffect: String,

    spanMethod: Function,

    selectOnIndeterminate: {
      type: Boolean,
      default: true
    },
    radio: {
      type: Boolean,
      default: false
    },
    indent: {
      type: Number,
      default: 16
    },

    treeProps: {
      type: Object,
      default () {
        return {
          hasChildren: 'hasChildren',
          children: 'children',
          hiddenTreeIcon: false,
          treeIconName: ''
        };
      }
    },

    lazy: Boolean,

    load: Function,

    headerSticky: Boolean,
    showFixColumns: {
      type: Boolean,
      default: true
    },

    loading: Boolean,
    totalRecord: {
      type: Number,
      default: 0
    },
    // 标记悬表头浮列
    markHoverColumn: {
      type: Boolean,
      default: false
    },
    // 标记排序后的列
    markSortedColumn: {
      type: Boolean,
      default: false
    },
    optimizeFixed: {
      type: [Boolean, Number],
      default: false
    },
    emptyCell: {
      type: String,
      default: '--'
    }
  },

  data () {
    const {
      hasChildren = 'hasChildren',
      children = 'children',
      hiddenTreeIcon = false,
      treeIconName = ''
    } = this.treeProps;
    this.store = createStore(this, {
      rowKey: this.rowKey,
      defaultExpandAll: this.defaultExpandAll,
      selectOnIndeterminate: this.selectOnIndeterminate,
      // TreeTable 的相关配置
      indent: this.indent,
      lazy: this.lazy,
      lazyColumnIdentifier: hasChildren,
      childrenColumnName: children,
      hiddenTreeIcon,
      treeIconName
    });
    const layout = new TableLayout({
      store: this.store,
      table: this,
      fit: this.fit,
      showHeader: this.showHeader
    });
    return {
      layout,
      isHidden: false,
      renderExpanded: null,
      resizeProxyVisible: false,
      resizeState: {
        width: null,
        height: null
      },
      // 是否拥有多级表头
      isGroup: false,
      scrollPosition: 'left'
    };
  },

  computed: {
    tableSize () {
      return this.size || (this.$ELEMENT || {}).size;
    },

    bodyWrapper () {
      return this.$refs.bodyWrapper;
    },

    shouldUpdateHeight () {
      return (
        this.height ||
        this.maxHeight ||
        this.fixedColumns.length > 0 ||
        this.rightFixedColumns.length > 0
      );
    },

    bodyWidth () {
      const { bodyWidth, scrollY, gutterWidth } = this.layout;
      if (bodyWidth) {
        return bodyWidth - (scrollY ? gutterWidth : 0) - 1 + 'px';
      }
      return '';
    },

    bodyHeight () {
      const { headerHeight = 0, bodyHeight, footerHeight = 0 } = this.layout;
      if (this.height) {
        return {
          height: bodyHeight ? bodyHeight + 'px' : ''
        };
      } else if (this.maxHeight) {
        const maxHeight = parseHeight(this.maxHeight);
        if (typeof maxHeight === 'number') {
          return {
            'max-height':
              maxHeight -
              footerHeight -
              (this.showHeader ? headerHeight : 0) +
              'px'
          };
        }
      }
      return {};
    },

    fixedBodyHeight () {
      if (this.height) {
        return {
          height: this.layout.fixedBodyHeight
            ? this.layout.fixedBodyHeight + 'px'
            : ''
        };
      } else if (this.maxHeight) {
        let maxHeight = parseHeight(this.maxHeight);
        if (typeof maxHeight === 'number') {
          maxHeight = this.layout.scrollX
            ? maxHeight - this.layout.gutterWidth
            : maxHeight;
          if (this.showHeader) {
            maxHeight -= this.layout.headerHeight;
          }
          maxHeight -= this.layout.footerHeight;
          return {
            'max-height': maxHeight + 'px',
            height: this.layout.fixedBodyHeight + 'px'
          };
        }
      }
      if (this.showSummary) {
        return {
          height: this.layout.tableHeight
            ? this.$refs.bodyWrapper.clientHeight + 'px'
            : ''
        };
      }
      return {
        height: this.layout.viewportHeight
          ? this.$refs.bodyWrapper.clientHeight + 'px'
          : ''
      };
    },

    fixedHeight () {
      if (this.maxHeight) {
        if (this.showSummary) {
          return {
            bottom: 0
          };
        }
        return {
          bottom:
            this.layout.scrollX && this.data.length
              ? this.layout.gutterWidth + 'px'
              : ''
        };
      } else {
        if (this.showSummary) {
          return {
            height: this.layout.tableHeight
              ? this.layout.tableHeight + 'px'
              : ''
          };
        }
        return {
          height: this.layout.viewportHeight
            ? this.layout.viewportHeight + 'px'
            : ''
        };
      }
    },

    emptyBlockStyle () {
      if (this.data && this.data.length) return null;
      const style = {
        width: this.bodyWidth
      };
      if (this.layout.appendHeight) {
        style.height = `calc(100% - ${this.layout.appendHeight}px)`;
      }
      return style;
    },
    hasOptimizeFixed () {
      if (typeof this.optimizeFixed === 'boolean') {
        return this.optimizeFixed;
      }
      return this.columns.length >= this.optimizeFixed;
    },

    ...mapStates({
      selection: 'selection',
      columns: 'columns',
      tableData: 'data',
      fixedColumns: 'fixedColumns',
      rightFixedColumns: 'rightFixedColumns'
    })
  },

  watch: {
    height: {
      immediate: true,
      handler (value) {
        this.layout.setHeight(value);
      }
    },

    maxHeight: {
      immediate: true,
      handler (value) {
        this.layout.setMaxHeight(value);
      }
    },

    currentRowKey: {
      immediate: true,
      handler (value) {
        if (!this.rowKey) return;
        this.store.setCurrentRowKey(value);
      }
    },

    data: {
      immediate: true,
      handler (value) {
        this.store.commit('setData', value);
      }
    },

    expandRowKeys: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.store.setExpandRowKeysAdapter(newVal);
        }
      }
    }
  },

  created () {
    this.tableId = 'el-table_' + tableIdSeed++;
    this.debouncedUpdateLayout = debounce(50, () => this.doLayout());
  },

  mounted () {
    this.bindEvents();
    this.store.updateColumns();
    this.doLayout();

    setTimeout(() => {
      if (this.rtXpcStickyContainer) {
        const container = this.rtXpcStickyContainer.getContainer();
        if (container) {
          container.addEventListener('scroll', this.updateLoadingTop)
        }
      }
    }, 300);

    this.resizeState = {
      width: this.$el.offsetWidth,
      height: this.$el.offsetHeight
    };

    // init filters
    this.store.states.columns.forEach(column => {
      if (column.filteredValue && column.filteredValue.length) {
        this.store.commit('filterChange', {
          column,
          values: column.filteredValue,
          silent: true
        });
      }
    });

    if (typeof ResizeObserver === 'function' && this.hasOptimizeFixed) {
      if (this.optimizeResizeOb) {
        this.optimizeResizeOb.disconnect();
      }

      const optimizeResizeOb = new ResizeObserver(() => {
        this.updateFixColumnsReact();
      });

      this.optimizeResizeOb = optimizeResizeOb;

      optimizeResizeOb.observe(this.$el);
    }

    this.$ready = true;
  },

  destroyed () {
    if (this.rtXpcStickyContainer) {
      const container = this.rtXpcStickyContainer.getContainer();
      if (container) {
        container.removeEventListener('scroll', this.updateLoadingTop)
      }
    }

    if (this.optimizeResizeOb) {
      this.optimizeResizeOb.disconnect();
    }
    this.unbindEvents();
  },

  methods: {
    getMigratingConfig () {
      return {
        events: {
          expand: 'expand is renamed to expand-change'
        }
      };
    },

    setCurrentRow (row) {
      this.store.commit('setCurrentRow', row);
    },

    toggleRowSelection (row, selected) {
      this.store.toggleRowSelection(row, selected, false);
      this.store.updateAllSelected();
    },

    toggleMultiRowSelection (rows) {
      this.store.toggleMultiRowSelection(rows, false);
      this.store.updateAllSelected();
    },

    toggleRowExpansion (row, expanded) {
      this.store.toggleRowExpansionAdapter(row, expanded);
    },

    clearSelection () {
      this.store.clearSelection();
    },

    clearFilter (columnKeys) {
      this.store.clearFilter(columnKeys);
    },

    clearSort () {
      this.store.clearSort();
    },

    handleMouseLeave () {
      this.store.commit('setHoverRow', null);
      this.store.commit('setHoverCol', null);
      if (this.hoverState) this.hoverState = null;
    },

    updateScrollY () {
      const changed = this.layout.updateScrollY();
      if (changed) {
        this.layout.notifyObservers('scrollable');
        this.layout.updateColumnsWidth();
      }
    },

    handleFixedMousewheel (event, data) {
      const bodyWrapper = this.bodyWrapper;
      if (Math.abs(data.spinY) > 0) {
        const currentScrollTop = bodyWrapper.scrollTop;
        if (data.pixelY < 0 && currentScrollTop !== 0) {
          event.preventDefault();
        }
        if (
          data.pixelY > 0 &&
          bodyWrapper.scrollHeight - bodyWrapper.clientHeight > currentScrollTop
        ) {
          event.preventDefault();
        }
        bodyWrapper.scrollTop += Math.ceil(data.pixelY / 5);
      } else {
        bodyWrapper.scrollLeft += Math.ceil(data.pixelX / 5);
      }
    },

    handleHeaderFooterMousewheel (event, data) {
      const { pixelX, pixelY } = data;
      if (Math.abs(pixelX) >= Math.abs(pixelY)) {
        this.bodyWrapper.scrollLeft += data.pixelX / 5;
      }
    },

    // 使用 CSS transform
    syncPostion: throttle(20, function () {
      const {
        scrollLeft,
        scrollTop,
        offsetWidth,
        scrollWidth
      } = this.bodyWrapper;
      const {
        headerWrapper,
        footerWrapper,
        fixedBodyWrapper,
        rightFixedBodyWrapper
      } = this.$refs;
      if (headerWrapper) headerWrapper.scrollLeft = scrollLeft;
      if (footerWrapper) footerWrapper.scrollLeft = scrollLeft;
      if (fixedBodyWrapper) fixedBodyWrapper.scrollTop = scrollTop;
      if (rightFixedBodyWrapper) rightFixedBodyWrapper.scrollTop = scrollTop;
      const maxScrollLeftPosition = scrollWidth - offsetWidth - 1;
      if (scrollLeft >= maxScrollLeftPosition) {
        this.scrollPosition = 'right';
      } else if (scrollLeft === 0) {
        this.scrollPosition = 'left';
      } else {
        this.scrollPosition = 'middle';
      }
    }),

    bindEvents () {
      this.bodyWrapper.addEventListener('scroll', this.syncPostion, {
        passive: true
      });
      if (this.fit) {
        addResizeListener(this.$el, this.resizeListener);
      }
    },

    unbindEvents () {
      this.bodyWrapper.removeEventListener('scroll', this.syncPostion, {
        passive: true
      });
      if (this.fit) {
        removeResizeListener(this.$el, this.resizeListener);
      }
    },

    resizeListener () {
      if (!this.$ready) return;
      let shouldUpdateLayout = false;
      const el = this.$el;
      const { width: oldWidth, height: oldHeight } = this.resizeState;

      const width = el.offsetWidth;
      if (oldWidth !== width) {
        shouldUpdateLayout = true;
      }

      const height = el.offsetHeight;
      if ((this.height || this.shouldUpdateHeight) && oldHeight !== height) {
        shouldUpdateLayout = true;
      }

      if (shouldUpdateLayout) {
        this.resizeState.width = width;
        this.resizeState.height = height;
        this.doLayout();
      }
    },

    doLayout () {
      if (this.shouldUpdateHeight) {
        this.layout.updateElsHeight();
      }
      this.layout.updateColumnsWidth();
      this.$setTimeout(() => {
        this.updateLoadingTop();
      }, 100)
    },

    sort (prop, order) {
      this.store.commit('sort', { prop, order });
    },

    toggleAllSelection () {
      this.store.commit('toggleAllSelection');
    },
    updateFixColumnsReact () {
      if (!this.$refs.headerWrapper || !this.$refs.bodyWrapper) {
        return;
      }

      const headerTr = this.$refs.headerWrapper.querySelector(
        '.el-table__header tr'
      );
      const bodyTrs = this.$refs.bodyWrapper.querySelectorAll(
        '.el-table__body tbody tr'
      );

      const headerHeight = headerTr.getBoundingClientRect().height;

      let leftFixBodyTrs;
      let rightFixBodyTrs;

      if (this.$refs.fixedWrapper) {
        const leftFixWrapper = this.$refs.fixedWrapper;
        const leftFixHeader = leftFixWrapper.querySelector(
          '.el-table__fixed-header-wrapper .el-table__header tr'
        );
        if (leftFixHeader) {
          leftFixHeader.style.height = headerHeight + 'px';
        }

        leftFixBodyTrs = leftFixWrapper.querySelectorAll(
          '.el-table__fixed-body-wrapper .el-table__body tbody tr'
        );
      }
      if (this.$refs.rightFixedWrapper) {
        const rightFixedWrapper = this.$refs.rightFixedWrapper;
        const rightFixHeader = rightFixedWrapper.querySelector(
          '.el-table__fixed-header-wrapper .el-table__header tr'
        );
        if (rightFixHeader) {
          rightFixHeader.style.height = headerHeight + 'px';
        }
        rightFixBodyTrs = rightFixedWrapper.querySelectorAll(
          '.el-table__fixed-body-wrapper .el-table__body tbody tr'
        );
      }

      bodyTrs.forEach((tr, index) => {
        const { height } = tr.getBoundingClientRect();
        if (leftFixBodyTrs && leftFixBodyTrs.length > 0) {
          const bodyTr = leftFixBodyTrs[index];
          bodyTr.style.height = height + 'px';
        }
        if (rightFixBodyTrs && rightFixBodyTrs.length > 0) {
          const bodyTr = rightFixBodyTrs[index];
          bodyTr.style.height = height + 'px';
        }
      });
    },
    updateLoadingTop () {
      if (!this.$refs.tableLoadingRef) {
        return;
      }
      const loadingEl = this.$refs.tableLoadingRef;

      /**
       * 不吸顶：
       1. 空数据、或表格高度不足320px，加载效果居中显示；
       2. 表格高度超过320px，加载效果距离顶部160px
       */
      if (!this.headerSticky) {
        const bodyHeight = this.$refs.bodyWrapper.clientHeight;

        if (bodyHeight < 320) {
          loadingEl.style.top = bodyHeight / 2 + 'px';
        } else {
          loadingEl.style.top = '160px';
        }
      } else {
        /**
         * 吸顶：
          加载效果视图可见（包括空数据）区域居中
         */

        const headerEl = this.$refs.headerWrapper;
        const rtTableEl = closest(this.$el, '.rt-table');
        const footerEl = rtTableEl.querySelector('.rt-table-footer');
        const tableBodyEl = rtTableEl.querySelector('.el-table__body-wrapper');

        const headerElRect = headerEl.getBoundingClientRect();
        let headerTop = parseInt(getComputedStyle(headerEl).top);
        if (isNaN(headerTop)) {
          headerTop = 0;
        }
        let clientHeight;
        if (footerEl) {
          const footerElRect = footerEl.getBoundingClientRect();

          clientHeight = footerElRect.top - headerElRect.bottom;
        } else {
          const tableBodyRect = tableBodyEl.getBoundingClientRect();
          const documentBodyRect = document.body.getBoundingClientRect();

          if (tableBodyRect.bottom > documentBodyRect.bottom) {
            clientHeight = documentBodyRect.bottom - headerElRect.bottom;
          } else {
            clientHeight = tableBodyRect.bottom - headerElRect.bottom;
          }
        }

        const top = (headerTop + clientHeight / 2 + headerElRect.height) - 8;

        loadingEl.style.top = top + 'px';
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// table.vue?5d476a1e